import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface ProposalStatus {
  proposal_status_id: number;
  proposal_status_display_name: string;
  proposal_status_display_key: string;
  proposal_status_order: number;
  tenant_id: number;
}

export interface Team {
  team_id: number;
  team_name: string;
}

const getProposalUrl = (tenantId: number) => {
  if (!tenantId) throw new Error("Tenant ID is required.");
  return `${API_BASE_URL}/proposal/?tenant_id=${tenantId}`;
};

const addTenantToProposal = <T extends { tenant_id?: number | null }>(
  proposal: Partial<T>,
  tenantId: number
): Partial<T> => {
  if (!tenantId) {
    throw new Error("Tenant ID is required.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...proposal,
    tenant_id: tenantId,
    created_by: currentUserId,
    updated_by: currentUserId,
  };
};

export interface Proposal {
  proposal_id?: number;
  crm_entity_id?: number;
  subject: string;
  created_datetime: string;
  updated_datetime: string;
  start_datetime?: string;
  expiration_datetime?: string;
  product_n_services?: string | null;
  amount?: number;
  grand_total?: number;
  currency_code: string;
  description?: string;
  created_by?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  updated_by?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  tenant_id?: number;
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;
  company?: {
    company_id: number;
    company_name: string;
    avatar_url?: string;
  };
  primary_contact?: {
    contact_id: number;
    full_name: string;
    first_name?: string;
    last_name?: string;
    primary_phone?: string;
    primary_email?: string;
    avatar_url?: string;
    job_title?: string;
  };
  assigned_user?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  assigned_team?: {
    team_id: number;
    team_name: string;
  };
  deal?: {
    deal_id: number;
    title: string;
  };
  proposal_status?: ProposalStatus;
  assigned_user_id?: number;
  assigned_team_id?: number;
  company_id?: number;
  deal_id?: number;
  proposal_status_id?: number;
  attachment_url?: string[];
  is_archive?: boolean;
  approvers?: number[];
}

export interface ProposalRequest {
  subject: string;
  start_datetime?: string;
  expiration_datetime?: string;
  product_n_services?: string;
  amount?: number;
  grand_total?: number;
  currency_code: string;
  description?: string;
  source?: string;
  tags?: {
    insert: string[];
  };
  assigned_user_id?: number;
  assigned_team_id?: number;
  company_id?: number;
  primary_contact?: number;
  deal_id?: number;
  proposal_status_id?: number;
  tenant_id?: number;
  created_by?: number;
  updated_by?: number;
  attachment_url?: File[];
}

export class ProposalService {
  static async getProposals(tenantId: number, page: number = 1, limit: number = 12, filters: Record<string, any> = {}): Promise<{ results: Proposal[]; count: number }> {
    try {
      const authHeaders = getAuthHeaders();
      let url = `${API_BASE_URL}/proposal/?tenant_id=${tenantId}&page=${page}&limit=${limit}`;

      // Handle archived filter
      if (filters.archivedFilter) {
        if (filters.archivedFilter === "yes") {
          url += `&is_archive=true`;
        } else if (filters.archivedFilter === "no") {
          url += `&is_archive=false`;
        }
        // For "both", don't add is_archive parameter
      } else {
        // Default to non-archived if no filter specified
        url += `&is_archive=false`;
      }

      // Add filter parameters
      if (filters) {
        Object.keys(filters).forEach((key) => {
          if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            return;
          }
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
            url += `&${key}=${encodeURIComponent(filters[key])}`;
          }
        });

        if (filters.searchTerm) {
          url += `&subject=${encodeURIComponent(filters.searchTerm)}`;
        }

        if (filters.dateRange && filters.dateRange[0]) {
          url += `&created_datetime_gte=${filters.dateRange[0].toISOString().split('T')[0]}`;
        }

        if (filters.dateRange && filters.dateRange[1]) {
          url += `&created_datetime_lte=${filters.dateRange[1].toISOString().split('T')[0]}`;
        }

        if (filters.statusFilter) {
          url += `&proposal_status_id=${encodeURIComponent(filters.statusFilter)}`;
        }
      }

      // Get the current auth data to check user role
      const authRaw = localStorage.getItem("authResponse");
      if (authRaw) {
        try {
          const auth = JSON.parse(authRaw);
          // Find the tenant entry matching our selectedTenantId
          const tenantObj = Array.isArray(auth.tenants)
            ? auth.tenants.find(
              (t: any) => String(t.tenant_id) === String(tenantId)
            )
            : null;

          // Only for SALESUSER role, add sales_user_id filter
          if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
            url += `&sales_user_id=${auth.user_id}`;
          }
        } catch (error) {
          console.error("Error parsing auth data:", error);
        }
      }

      const response = await axios.get(url, { headers: authHeaders });
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async createProposal(proposalData: Partial<ProposalRequest>): Promise<Proposal> {
    try {
      const authHeaders = getAuthHeaders();
      const authResponse = localStorage.getItem("authResponse");

      if (!authResponse) {
        throw new Error("Authentication required");
      }

      const auth = JSON.parse(authResponse);
      const selectedTenantId = localStorage.getItem("selectedTenantId");

      if (!selectedTenantId) {
        throw new Error("Tenant ID is required");
      }

      const proposalWithTenant = addTenantToProposal(proposalData, Number(selectedTenantId));

      // Check if we have attachments to upload
      const { attachment_url, ...proposalFields } = proposalWithTenant;

      if (attachment_url && Array.isArray(attachment_url) && attachment_url.length > 0) {
        // Use FormData for file upload
        const formData = new FormData();

        // Add all proposal fields
        Object.entries(proposalFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        });

        // Add files
        attachment_url.forEach((file: File) => {
          formData.append('attachment_url', file);
        });

        // For FormData, don't set Content-Type header - let browser set it with boundary
        const { 'Content-Type': _, ...fileUploadHeaders } = authHeaders;

        const response = await axios.post(
          `${API_BASE_URL}/proposal/`,
          formData,
          { headers: fileUploadHeaders }
        );

        return response.data;
      } else {
        // Regular JSON request without files
        const response = await axios.post(
          `${API_BASE_URL}/proposal/`,
          proposalWithTenant,
          { headers: authHeaders }
        );

        return response.data;
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async updateProposal(proposalId: number, proposalData: Partial<ProposalRequest>): Promise<Proposal> {
    try {
      const authHeaders = getAuthHeaders();
      const authResponse = localStorage.getItem("authResponse");

      if (!authResponse) {
        throw new Error("Authentication required");
      }

      const auth = JSON.parse(authResponse);
      const selectedTenantId = localStorage.getItem("selectedTenantId");

      if (!selectedTenantId) {
        throw new Error("Tenant ID is required");
      }

      const proposalWithTenant = addTenantToProposal(proposalData, Number(selectedTenantId));

      // Check if we have attachments to upload
      const { attachment_url, ...proposalFields } = proposalWithTenant;

      if (attachment_url && Array.isArray(attachment_url) && attachment_url.length > 0) {
        // Use FormData for file upload
        const formData = new FormData();

        // Add all proposal fields
        Object.entries(proposalFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        });

        // Add files
        attachment_url.forEach((file: File) => {
          formData.append('attachment_url', file);
        });

        // For FormData, don't set Content-Type header - let browser set it with boundary
        const { 'Content-Type': _, ...fileUploadHeaders } = authHeaders;

        const response = await axios.patch(
          `${API_BASE_URL}/proposal/${proposalId}/`,
          formData,
          { headers: fileUploadHeaders }
        );

        return response.data;
      } else {
        // Regular JSON request without files
        const response = await axios.patch(
          `${API_BASE_URL}/proposal/${proposalId}/`,
          proposalWithTenant,
          { headers: authHeaders }
        );

        return response.data;
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async deleteProposal(proposalId: number, tenantId: number): Promise<void> {
    try {
      const authHeaders = getAuthHeaders();

      await axios.delete(
        `${API_BASE_URL}/proposal/${proposalId}/`,
        { headers: authHeaders }
      );
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getProposalById(proposalId: number): Promise<Proposal> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/proposal/${proposalId}/`,
        { headers: authHeaders }
      );

      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getTeams(tenantId: number): Promise<Team[]> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/team/?tenant_id=${tenantId}&response_mode=light`,
        { headers: authHeaders }
      );

      return response.data.results || [];
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getProposalStatuses(tenantId: number): Promise<ProposalStatus[]> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/proposal-status/?tenant_id=${tenantId}`,
        { headers: authHeaders }
      );

      return response.data.results || [];
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }
}
